import DOMPurify from 'isomorphic-dompurify';

/**
 * Sanitize HTML content with strict allowlist for Quill editor output
 * Allows only: bold, italic, ordered/bullet lists, blockquote, indent/outdent, align, links
 * Removes: images, scripts, and all other potentially unsafe content
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  // Configure DOMPurify with strict allowlist matching Quill's limited formatting
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',        // Paragraphs
      'br',       // Line breaks
      'strong',   // Bold
      'em',       // Italic
      'ul',       // Unordered list
      'ol',       // Ordered list
      'li',       // List items
      'blockquote', // Blockquote
      'a',        // Links
    ],
    ALLOWED_ATTR: [
      'class',    // Quill classes: ql-indent-*, ql-align-*
      'href',     // Link URL
      'target',   // Optional (if added later)
      'rel',      // Optional (if added later)
    ],
    ALLOW_DATA_ATTR: false,
    // Allow only safe protocols for links
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  });

  return clean;
}

/**
 * Sanitize on save - use before storing to database
 */
export function sanitizeForSave(html: string): string {
  return sanitizeHtml(html);
}

/**
 * Sanitize on render - use before displaying in UI
 * Double sanitization for extra security
 */
export function sanitizeForRender(html: string): string {
  return sanitizeHtml(html);
}

/**
 * Strip all HTML tags from text and return plain text
 * Useful for creating excerpts from HTML content
 */
export function stripHtmlTags(html: string): string {
  if (!html) return '';

  // Preserve natural spacing where block-level tags are removed.
  const withSeparators = html.replace(
    /<\/?(p|div|br|li|ul|ol|blockquote|h[1-6])[^>]*>/gi,
    ' '
  );
  
  // First, use DOMPurify to sanitize (removes dangerous content)
  const sanitized = DOMPurify.sanitize(withSeparators, {
    ALLOWED_TAGS: [], // Remove all tags but keep content
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });

  // Then strip remaining HTML tags and decode entities
  return sanitized
    .replace(/<[^>]*>/g, '') // Remove any remaining HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/&#x27;/g, "'") // Replace &#x27; with '
    .replace(/&#x2F;/g, '/') // Replace &#x2F; with /
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
}
